import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { Calendar, Clock, User, Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { format, parseISO, isPast } from 'date-fns';
import { de } from 'date-fns/locale';

interface Slot {
  id: number;
  start_time: string;
  end_time: string;
  status: string;
  reserved_until: string | null;
}

interface Config {
  id: number;
  title: string;
  description: string | null;
  slot_duration_minutes: number;
  appointment_type: string;
  first_name: string;
  last_name: string;
}

interface BookingData {
  slot_id: number;
  parent_name: string;
  parent_email: string;
  student_name?: string;
  notes?: string;
}

export default function AppointmentsPublic() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState<BookingData>({
    slot_id: 0,
    parent_name: '',
    parent_email: '',
    student_name: '',
    notes: ''
  });

  // Fetch public appointment data
  const { data, isLoading, error } = useQuery<{ config: Config; slots: Slot[] }>(
    ['appointments', 'public', token],
    async () => {
      const response = await api.get(`/appointments/public/${token}`);
      return response.data.data;
    },
    { enabled: !!token, retry: 1 }
  );

  // Booking mutation
  const bookingMutation = useMutation(
    async (data: BookingData) => {
      const response = await api.post('/appointments/public/book', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Buchung erstellt! Bitte bestätigen Sie per E-Mail-Link.');
        setShowBookingModal(false);
        setSelectedSlotId(null);
        setBookingData({ slot_id: 0, parent_name: '', parent_email: '', student_name: '', notes: '' });
        // Refetch slots
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Buchung');
      }
    }
  );

  const handleSlotClick = (slot: Slot) => {
    if (slot.status !== 'available') {
      toast.error('Dieser Slot ist nicht verfügbar');
      return;
    }
    if (slot.reserved_until && new Date(slot.reserved_until) > new Date()) {
      toast.error('Dieser Slot ist bereits reserviert');
      return;
    }
    setSelectedSlotId(slot.id);
    setBookingData(prev => ({ ...prev, slot_id: slot.id }));
    setShowBookingModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Terminbuchung nicht gefunden</h1>
          <p className="text-gray-600 mb-4">
            Die angeforderte Terminbuchung existiert nicht oder ist nicht mehr aktiv.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  const { config, slots } = data;
  const availableSlots = slots.filter(slot => {
    if (slot.status !== 'available') return false;
    if (isPast(parseISO(slot.start_time))) return false;
    if (slot.reserved_until && new Date(slot.reserved_until) > new Date()) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold mb-2">{config.title}</h1>
          <p className="text-gray-600 mb-4">
            {config.first_name} {config.last_name}
          </p>
          {config.description && (
            <p className="text-gray-700 mb-4">{config.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {config.slot_duration_minutes} Minuten pro Termin
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {config.appointment_type === 'parent_teacher_conference' ? 'Elternsprechtag' : 'Sprechstunde'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Verfügbare Termine</h2>
          
          {availableSlots.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Derzeit sind keine Termine verfügbar.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableSlots.map(slot => (
                <button
                  key={slot.id}
                  onClick={() => handleSlotClick(slot)}
                  className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition text-left"
                >
                  <div className="font-semibold text-lg mb-1">
                    {format(parseISO(slot.start_time), 'dd.MM.yyyy', { locale: de })}
                  </div>
                  <div className="text-gray-600">
                    {format(parseISO(slot.start_time), 'HH:mm', { locale: de })} -{' '}
                    {format(parseISO(slot.end_time), 'HH:mm', { locale: de })}
                  </div>
                  <div className="mt-2 text-sm text-green-600 font-medium">Verfügbar</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Termin buchen</h3>
            <div className="space-y-4">
              {selectedSlotId && (
                <div className="p-3 bg-gray-50 rounded">
                  <div className="font-semibold">
                    {slots.find(s => s.id === selectedSlotId) && (
                      <>
                        {format(parseISO(slots.find(s => s.id === selectedSlotId)!.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} -{' '}
                        {format(parseISO(slots.find(s => s.id === selectedSlotId)!.end_time), 'HH:mm', { locale: de })}
                      </>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Ihr Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bookingData.parent_name}
                  onChange={(e) => setBookingData({ ...bookingData, parent_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Ihre E-Mail-Adresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={bookingData.parent_email}
                  onChange={(e) => setBookingData({ ...bookingData, parent_email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name des Schülers (optional)</label>
                <input
                  type="text"
                  value={bookingData.student_name}
                  onChange={(e) => setBookingData({ ...bookingData, student_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notizen (optional)</label>
                <textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                Sie erhalten eine E-Mail mit einem Bestätigungslink. Bitte bestätigen Sie innerhalb von 10 Minuten, sonst wird der Termin wieder freigegeben.
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedSlotId(null);
                }}
                className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  // Ensure slot_id is set from selectedSlotId
                  if (!selectedSlotId) {
                    toast.error('Bitte wählen Sie einen Slot aus');
                    return;
                  }
                  bookingMutation.mutate({
                    ...bookingData,
                    slot_id: selectedSlotId
                  });
                }}
                disabled={!selectedSlotId || !bookingData.parent_name || !bookingData.parent_email || bookingMutation.isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {bookingMutation.isLoading ? 'Wird gebucht...' : 'Buchen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

